import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import app from "../firebaseConfig";
import { getDatabase, ref, get } from "firebase/database";
import { FaStar, FaChevronDown, FaUser } from "react-icons/fa";

const FeedbackList = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedRating, setSelectedRating] = useState(5);
  const [visibleCount, setVisibleCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expanded, setExpanded] = useState(false);
  const feedbackSectionRef = useRef(null);

  useEffect(() => {
    const db = getDatabase(app);
    const feedbackRef = ref(db, "urocare/feedback");

    const fetchData = async () => {
      try {
        const snapshot = await get(feedbackRef);
        if (snapshot.exists()) {
          const data = snapshot.val() || {};
          const feedbackArray = Object.entries(data).map(([id, feedback]) => ({ id, ...feedback }));
          setFeedbacks(feedbackArray);
        } else {
          setFeedbacks([]);
        }
      } catch (error) {
        console.error("Error fetching feedback:", error);
      }
    };

    fetchData();

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const differenceInSeconds = Math.floor((now - past) / 1000);

    const minutes = Math.floor(differenceInSeconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);

    if (months > 0) return `${months} month${months > 1 ? "s" : ""} ago`;
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    return "Just now";
  };

  const filteredFeedbacks = feedbacks
    .filter((f) => f.rating === selectedRating)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const defaultVisibleCount = isMobile ? 3 : 9;

  useEffect(() => {
    setVisibleCount(defaultVisibleCount);
    setExpanded(false);
  }, [selectedRating, defaultVisibleCount]);

  const handleShowMore = () => {
    const extraCount = isMobile ? 3 : 9;
    const remainingFeedbacks = filteredFeedbacks.length - visibleCount;

    if (remainingFeedbacks > 0) {
      setVisibleCount((prevCount) => prevCount + Math.min(extraCount, remainingFeedbacks));
    }
  };

  const handleShowLess = () => {
    setVisibleCount(defaultVisibleCount);
    setExpanded(false);
    if (feedbackSectionRef.current) {
      feedbackSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Patient Feedback - Dr. Srikanth Urologist</title>
        <meta name="description" content="Read real patient feedback and reviews about Dr. Srikanth UroCare Clinic." />
        <link rel="canonical" href="https://www.yoursite.com/feedback" />
      </Helmet>

      <div ref={feedbackSectionRef} className="min-h-screen flex flex-col items-center bg-gray-100 p-6">
        <div className="w-full max-w-7xl flex justify-between items-center mb-6">
          <h1 className="text-4xl font-bold text-gray-700">Patient Feedback</h1>
          <div className="relative">
            <button className="flex items-center px-5 py-3 bg-blue-600 text-white font-semibold rounded-lg"
              onClick={() => setDropdownOpen(!dropdownOpen)}>
              {selectedRating} Star <FaChevronDown className="ml-2" />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white shadow-lg rounded-md">
                {[5, 4, 3, 2, 1].map((rating) => (
                  <button key={rating} className="block w-full px-4 py-2 hover:bg-gray-200"
                    onClick={() => { setSelectedRating(rating); setDropdownOpen(false); }}>
                    {rating} Star
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {filteredFeedbacks.length > 0 ? (
          <div className="max-w-7xl w-full">
            <h2 className="text-3xl font-semibold text-gray-600 mb-4">See What Patients Say</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredFeedbacks.slice(0, visibleCount).map((item, index) => (
                <div key={index} className="feedback-card p-6 bg-white shadow-lg rounded-xl border">
                  <p className="text-sm text-gray-500 font-semibold">{getTimeAgo(item.timestamp)}</p>
                  <div className="flex items-center mt-1">
                    <FaUser className="text-gray-500 mr-2" size={20} />
                    <h3 className="text-xl font-semibold text-gray-800">{item.name}</h3>
                  </div>
                  <div className="mt-2 max-h-32 overflow-y-auto p-3 bg-gray-50 rounded-lg">
                    <p className="text-gray-600 text-lg">{item.feedback}</p>
                  </div>
                  <div className="flex mt-3">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={24} color={i < item.rating ? "#FFD700" : "#e4e5e9"} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-6">
              {visibleCount < filteredFeedbacks.length && (
                <button onClick={handleShowMore} className="px-6 py-3 bg-blue-500 text-white rounded-lg">
                  View More
                </button>
              )}
              {visibleCount > defaultVisibleCount && (
                <button onClick={handleShowLess} className="px-6 py-3 bg-red-500 text-white rounded-lg ml-4">
                  View Less
                </button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-600">No feedback available.</p>
        )}
      </div>
    </>
  );
};

export default FeedbackList;
